import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { Code as CodeIcon } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'

import { ScriptValueDialog, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import {
  buildFormScriptVariables,
  COMMON_SCRIPT_VARIABLES,
} from '@mobileConstants'
import { DrawerItem, MobileEventFlow, MOBILE_EVENT_CODE, Variable } from '@mobileTypes'

interface EventFlowFormDefaultValues {
  eventCode?: MOBILE_EVENT_CODE | ''
  buttonId?: string
  fieldId?: string
  intervalId?: string
  drawerItemIndex?: number
  elementId?: string
  variableName?: string
  variableScope?: 'local' | 'global' | 'background' | 'sheetForm'
  conditionBranchId?: string
  openModalBeforeTrigger?: boolean
  modalType?: 'confirmation' | 'comment' | 'picker'
  modalTitle?: string
  modalText?: string
  modalCommentVariable?: string
  modalSourceVariable?: string
  modalDisplayedValue?: string
  modalTargetVariable?: string
  showModalIfJs?: string
}

interface EventFlowConfigDialogProps {
  open: boolean
  eventFlow: MobileEventFlow | null
  formElements?: any[] // Form elements for button/swipe selection
  drawerItems?: DrawerItem[] // Drawer items for ON_DRAWER_ITEM_PRESS selection
  allowedEventCodes?: MOBILE_EVENT_CODE[]
  /** For ON_VARIABLE_CHANGE: variables to choose from (background, sheet form, global) */
  variables?: Array<{ id?: string; internalId: string }>
  globalVariables?: Array<{ id?: string; internalId: string }>
  backgroundVariables?: Array<{ id?: string; internalId: string }>
  sheetFormVariables?: Array<{ id?: string; internalId: string }>
  /** When creating new flow (eventFlow is null), pre-fill form with these values */
  defaultValues?: EventFlowFormDefaultValues
  /** When true, element selector (drawer item / button / swipe / field) is disabled (e.g. when creating flow from element modal) */
  lockElementSelection?: boolean
  onClose: () => void
  onSave: (eventFlow: MobileEventFlow) => void
}

interface EventFlowFormData {
  id: string
  eventCode: MOBILE_EVENT_CODE | ''
  buttonId?: string
  fieldId?: string
  intervalId?: string
  drawerItemIndex?: number
  elementId?: string
  variableName?: string
  variableScope?: 'local' | 'global' | 'background' | 'sheetForm'
  conditionBranchId?: string
  openModalBeforeTrigger?: boolean
  modalType?: 'confirmation' | 'comment' | 'picker'
  modalTitle?: string
  modalText?: string
  modalCommentVariable?: string
  modalSourceVariable?: string
  modalDisplayedValue?: string
  modalTargetVariable?: string
  showModalIfJs?: string
}

const varKey = (v: { internalId: string }) => v.internalId

export const EventFlowConfigDialog = ({
  open,
  eventFlow,
  formElements = [],
  drawerItems = [],
  allowedEventCodes,
  variables = [],
  globalVariables = [],
  backgroundVariables = [],
  sheetFormVariables = [],
  defaultValues: defaultValuesProp,
  lockElementSelection = false,
  onClose,
  onSave,
}: EventFlowConfigDialogProps) => {
  const { control, handleSubmit, reset, watch, setValue } = useForm<EventFlowFormData>({
    defaultValues: {
      id: '',
      eventCode: '',
      buttonId: '',
      fieldId: '',
      intervalId: '',
      drawerItemIndex: undefined,
      elementId: '',
      variableName: '',
      variableScope: 'local' as const,
      conditionBranchId: '',
      openModalBeforeTrigger: false,
      modalType: 'confirmation',
      modalTitle: '',
      modalText: '',
      modalCommentVariable: '',
      modalSourceVariable: '',
      modalDisplayedValue: '',
      modalTargetVariable: '',
      showModalIfJs: '',
    },
  })

  const watchedModalType = watch('modalType')
  const watchedOpenModalBeforeTrigger = watch('openModalBeforeTrigger')
  
  const watchedEventCode = watch('eventCode')

  const [showShowModalIfJsDialog, setShowShowModalIfJsDialog] = useState(false)
  const formVariablesForScript = useMemo(
    () =>
      (variables || []).map(v => ({
        ...v,
        variableType: 'single' as const,
        valueType: 'any',
      })),
    [variables]
  )
  const formGlobalVariablesForScript = useMemo(
    () =>
      (globalVariables || []).map(v => ({
        ...v,
        variableType: 'single' as const,
        valueType: 'any',
      })),
    [globalVariables]
  )
  const scriptVariablesForShowModalIf = useMemo(() => {
    const base = buildFormScriptVariables(
      formVariablesForScript as Variable[],
      formGlobalVariablesForScript as Variable[]
    )
    const eventDataChildren = base.filter(v =>
      ['formData', 'item', 'localVariables', 'globalVariables'].includes(v.name)
    )
    return [
      {
        name: 'eventData',
        type: eventDataChildren,
        description:
          'Event context (item, formData, localVariables, globalVariables)',
      },
      ...COMMON_SCRIPT_VARIABLES,
    ]
  }, [formVariablesForScript, formGlobalVariablesForScript])
  const scriptFieldsInfoForShowModalIf = useMemo(
    () => variablesToFieldsInfo(scriptVariablesForShowModalIf),
    [scriptVariablesForShowModalIf]
  )

  const variableOptionsForOnVariableChange = useMemo(() => {
    const global = globalVariables.map(v => ({ ...v, isGlobal: true, variableScope: 'global' as const }))
    const background =
      backgroundVariables?.map(v => ({
        ...v,
        id: `bg-${v.id ?? v.internalId}`,
        internalId: varKey(v),
        variableScope: 'background' as const,
      })) ?? []
    const sheetForm =
      sheetFormVariables?.map(v => ({
        ...v,
        id: `sheet-${v.id ?? v.internalId}`,
        internalId: varKey(v),
        variableScope: 'sheetForm' as const,
      })) ?? []
    const local = variables.map(v => ({ ...v, isGlobal: false, variableScope: 'local' as const }))
    return [...local, ...global, ...background, ...sheetForm]
  }, [variables, globalVariables, backgroundVariables, sheetFormVariables])

  const eventCodeOptions = useMemo(() => {
    const base = Object.entries(MOBILE_EVENT_CODE)
      .map(([key, value]) => ({ label: key.replace(/_/g, ' '), value }))

    if (!allowedEventCodes || allowedEventCodes.length === 0) {
      return base
    }

    const allowed = new Set(allowedEventCodes)
    return base.filter(opt => allowed.has(opt.value))
  }, [allowedEventCodes])
  
  // Get available buttons/swipes for selection
  const availableButtons = useMemo(() => {
    return formElements.filter((el: any) => el.type === 'BUTTON' && el.id)
  }, [formElements])
  
  const availableSwipes = useMemo(() => {
    return formElements.filter((el: any) => el.type === 'SWIPE' && el.id)
  }, [formElements])
  
  const availableFields = useMemo(() => {
    return formElements.filter((el: any) => el.type === 'FIELD' && el.id)
  }, [formElements])

  const availableEmbeddedLists = useMemo(() => {
    return formElements.filter((el: any) => el.type === 'EMBEDDED_LIST' && el.id)
  }, [formElements])

  const needsButtonSelection = watchedEventCode === MOBILE_EVENT_CODE.ON_BUTTON_PRESS
  const needsSwipeSelection = watchedEventCode === MOBILE_EVENT_CODE.ON_BUTTON_SWIPE
  const needsFieldSelection = watchedEventCode === MOBILE_EVENT_CODE.ON_FIELD_VALUE_CHANGE
  const needsIntervalSelection = watchedEventCode === MOBILE_EVENT_CODE.ON_INTERVAL
  const needsDrawerItemSelection = watchedEventCode === MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS
  const needsEmbeddedListSelection =
    watchedEventCode === MOBILE_EVENT_CODE.ON_CHECKBOX_EMBEDDED_LIST_CHANGE ||
    watchedEventCode === MOBILE_EVENT_CODE.ON_EMBEDDED_LIST_ITEM_CONTEXT_MENU
  const needsVariableSelection = watchedEventCode === MOBILE_EVENT_CODE.ON_VARIABLE_CHANGE
  const needsConditionBranchId = watchedEventCode === MOBILE_EVENT_CODE.ON_CONDITION

  useEffect(() => {
    if (open) {
      if (eventFlow) {
        reset({
          id: eventFlow.id,
          eventCode: eventFlow.eventCode,
          buttonId: eventFlow.buttonId || '',
          fieldId: eventFlow.fieldId || '',
          intervalId: eventFlow.intervalId || '',
          drawerItemIndex: eventFlow.drawerItemIndex ?? undefined,
          elementId: eventFlow.elementId || '',
          variableName: eventFlow.variableName || '',
          variableScope: eventFlow.variableScope || 'local',
          conditionBranchId: eventFlow.conditionBranchId || '',
          openModalBeforeTrigger: eventFlow.openModalBeforeTrigger ?? false,
          modalType: eventFlow.modalType || 'confirmation',
          modalTitle: eventFlow.modalTitle || '',
          modalText: eventFlow.modalText || '',
          modalCommentVariable: eventFlow.modalCommentVariable || '',
          modalSourceVariable: eventFlow.modalSourceVariable || '',
          modalDisplayedValue: eventFlow.modalDisplayedValue || '',
          modalTargetVariable: eventFlow.modalTargetVariable || '',
          showModalIfJs: eventFlow.showModalIfJs || '',
        })
      } else {
        const base = {
          id: uuidv4(),
          eventCode: '',
          buttonId: '',
          fieldId: '',
          intervalId: '',
          drawerItemIndex: undefined,
          elementId: '',
          variableName: '',
          variableScope: 'local' as const,
          conditionBranchId: '',
          openModalBeforeTrigger: false,
          modalType: 'confirmation' as const,
          modalTitle: '',
          modalText: '',
          modalCommentVariable: '',
          modalSourceVariable: '',
          modalDisplayedValue: '',
          modalTargetVariable: '',
          showModalIfJs: '',
        }
        reset((defaultValuesProp ? { ...base, ...defaultValuesProp } : base) as EventFlowFormData)
      }
    }
  }, [open, eventFlow, defaultValuesProp, reset])

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    event.stopPropagation()

    handleSubmit((data: EventFlowFormData) => {
      if (!data.eventCode) {
        return
      }

      const flowData: MobileEventFlow = {
        id: data.id,
        eventCode: data.eventCode as MOBILE_EVENT_CODE,
        actions: eventFlow?.actions || [],
      }
      
      // Add buttonId/fieldId/intervalId if provided
      if (data.buttonId && data.buttonId.trim()) {
        flowData.buttonId = data.buttonId.trim()
      }
      if (data.fieldId && data.fieldId.trim()) {
        flowData.fieldId = data.fieldId.trim()
      }
      if (data.intervalId && data.intervalId.trim()) {
        flowData.intervalId = data.intervalId.trim()
      }
      if (needsDrawerItemSelection && data.drawerItemIndex !== undefined && data.drawerItemIndex !== null) {
        flowData.drawerItemIndex = Number(data.drawerItemIndex)
      }
      if (data.elementId && data.elementId.trim()) {
        flowData.elementId = data.elementId.trim()
      }
      if (needsVariableSelection && data.variableName && data.variableName.trim()) {
        flowData.variableName = data.variableName.trim()
        if (data.variableScope && data.variableScope !== 'local') {
          flowData.variableScope = data.variableScope
        }
      }
      if (needsConditionBranchId && data.conditionBranchId && data.conditionBranchId.trim()) {
        flowData.conditionBranchId = data.conditionBranchId.trim()
      }

      if (data.openModalBeforeTrigger) {
        flowData.openModalBeforeTrigger = true
        flowData.modalType = (data.modalType || 'confirmation') as 'confirmation' | 'comment' | 'picker'
        if (data.modalTitle != null && data.modalTitle.trim()) {
          flowData.modalTitle = data.modalTitle.trim()
        }
        if (data.modalText != null && data.modalText.trim()) {
          flowData.modalText = data.modalText.trim()
        }
        if (data.modalType === 'comment' && data.modalCommentVariable && data.modalCommentVariable.trim()) {
          flowData.modalCommentVariable = data.modalCommentVariable.trim()
        }
        if (data.modalType === 'picker') {
          if (data.modalSourceVariable?.trim()) {
            flowData.modalSourceVariable = data.modalSourceVariable.trim()
          }
          if (data.modalDisplayedValue != null && data.modalDisplayedValue.trim()) {
            flowData.modalDisplayedValue = data.modalDisplayedValue.trim()
          }
          if (data.modalTargetVariable?.trim()) {
            flowData.modalTargetVariable = data.modalTargetVariable.trim()
          }
        }
        if (data.showModalIfJs != null && data.showModalIfJs.trim()) {
          flowData.showModalIfJs = data.showModalIfJs.trim()
        }
      }

      onSave(flowData)
      onClose()
    })(event)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{eventFlow ? 'Edit Event Flow' : 'Add Event Flow'}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name='id'
            control={control}
            rules={{ required: 'ID is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label='Flow ID'
                fullWidth
                disabled={!!eventFlow}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'Unique identifier for this event flow'}
              />
            )}
          />

          <Controller
            name='eventCode'
            control={control}
            rules={{ required: 'Event code is required' }}
            render={({ field, fieldState }) => (
              <Autocomplete
                value={eventCodeOptions.find(opt => opt.value === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.value || '')
                }}
                options={eventCodeOptions}
                getOptionLabel={option => option.label}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Event Code'
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />

          {/* Button/Swipe selector for ON_BUTTON_PRESS and ON_BUTTON_SWIPE events */}
          {(needsButtonSelection || needsSwipeSelection) && (
            <Controller
              name='buttonId'
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  disabled={lockElementSelection}
                  value={
                    (needsButtonSelection ? availableButtons : availableSwipes).find(
                      (el: any) => el.id === field.value
                    ) || null
                  }
                  onChange={(_, newValue: any) => {
                    field.onChange(newValue?.id || '')
                  }}
                  options={needsButtonSelection ? availableButtons : availableSwipes}
                  getOptionLabel={(option: any) => 
                    `${option.id}${option.title ? ` - ${option.title}` : ''}`
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={needsButtonSelection ? 'Button' : 'Swipe Element'}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message || 
                        `Select which ${needsButtonSelection ? 'button' : 'swipe'} triggers this event (optional - leave empty for all)`
                      }
                    />
                  )}
                />
              )}
            />
          )}

          {/* Field selector for ON_FIELD_VALUE_CHANGE */}
          {needsFieldSelection && (
            <Controller
              name='fieldId'
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  disabled={lockElementSelection}
                  value={availableFields.find((el: any) => el.id === field.value) || null}
                  onChange={(_, newValue: any) => {
                    field.onChange(newValue?.id || '')
                  }}
                  options={availableFields}
                  getOptionLabel={(option: any) =>
                    `${option.id}${option.title ? ` - ${option.title}` : ''}`
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Field'
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        'Select which field triggers this event (optional - leave empty for all)'
                      }
                    />
                  )}
                />
              )}
            />
          )}

          {/* Interval ID input for ON_INTERVAL */}
          {needsIntervalSelection && (
            <Controller
              name='intervalId'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Interval ID'
                  fullWidth
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    'Filter by timer ID (optional - leave empty to handle all intervals). Must match the intervalId used in START_INTERVAL action.'
                  }
                  placeholder='e.g., gps_timer, sync_timer'
                />
              )}
            />
          )}

          {/* Embedded list selector for ON_CHECKBOX_EMBEDDED_LIST_CHANGE / ON_EMBEDDED_LIST_ITEM_CONTEXT_MENU */}
          {needsEmbeddedListSelection && (
            <Controller
              name='elementId'
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  disabled={lockElementSelection}
                  value={availableEmbeddedLists.find((el: any) => el.id === field.value) || null}
                  onChange={(_, newValue: any) => {
                    field.onChange(newValue?.id || '')
                  }}
                  options={availableEmbeddedLists}
                  getOptionLabel={(option: any) =>
                    `${option.id}${option.title ? ` - ${option.title}` : option.id || ''}`
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Embedded List'
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        (lockElementSelection
                          ? 'Embedded list is fixed for this flow (created from element modal)'
                          : 'Select which embedded list triggers this event')
                      }
                    />
                  )}
                />
              )}
            />
          )}

          {/* Variable selector for ON_VARIABLE_CHANGE (background, sheet form, global) */}
          {needsVariableSelection && variableOptionsForOnVariableChange.length > 0 && (
            <Controller
              name='variableName'
              control={control}
              render={({ field }) => {
                const watchedScope = watch('variableScope')
                return (
                  <Autocomplete
                    value={
                      variableOptionsForOnVariableChange.find(
                        v =>
                          v.internalId === field.value &&
                          ((v as { variableScope?: string }).variableScope ?? 'local') === watchedScope
                    ) || null
                    }
                    onChange={(_, newValue: any) => {
                      const scope = (newValue?.variableScope ?? 'local') as 'local' | 'global' | 'background' | 'sheetForm'
                      setValue('variableScope', scope)
                      field.onChange(newValue?.internalId ?? '')
                    }}
                    options={variableOptionsForOnVariableChange}
                    groupBy={(option: any) => {
                      const scope = option.variableScope
                      if (scope === 'background') return 'Background Variables'
                      if (scope === 'sheetForm') return 'Sheet Form Variables'
                      return option.isGlobal ? 'Global Variables' : 'Local Variables'
                    }}
                    getOptionLabel={(option: any) => option.internalId ?? ''}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label='Variable'
                        helperText='Listen to specific variable (optional - leave empty for all)'
                      />
                    )}
                  />
                )
              }}
            />
          )}

          {/* Condition branch ID for ON_CONDITION */}
          {needsConditionBranchId && (
            <Controller
              name='conditionBranchId'
              control={control}
              rules={{ required: 'Branch ID is required for ON_CONDITION flows' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Condition Branch ID'
                  fullWidth
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    'Unique branch identifier referenced by CONDITION action (e.g. branch_true, branch_admin)'
                  }
                  placeholder='e.g., branch_true, branch_active'
                />
              )}
            />
          )}

          {/* Modal before trigger */}
          <Controller
            name='openModalBeforeTrigger'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  />
                }
                label='Open modal before event trigger'
              />
            )}
          />

          {watchedOpenModalBeforeTrigger && (
            <>
              <Controller
                name='modalType'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    value={
                      field.value === 'comment'
                        ? { value: 'comment', label: 'Comment' }
                        : field.value === 'picker'
                          ? { value: 'picker', label: 'Picker' }
                          : { value: 'confirmation', label: 'Confirmation' }
                    }
                    onChange={(_, newValue: { value: string; label: string } | null) => {
                      field.onChange((newValue?.value as 'confirmation' | 'comment' | 'picker') || 'confirmation')
                    }}
                    options={[
                      { value: 'confirmation', label: 'Confirmation' },
                      { value: 'comment', label: 'Comment' },
                      { value: 'picker', label: 'Picker' },
                    ]}
                    getOptionLabel={opt => opt.label}
                    renderInput={params => (
                      <TextField {...params} label='Modal type' fullWidth />
                    )}
                  />
                )}
              />
              <Controller
                name='modalTitle'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Modal title'
                    fullWidth
                    placeholder='e.g. Confirm action'
                  />
                )}
              />
              <Controller
                name='modalText'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Modal text'
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder='Description shown in the modal'
                  />
                )}
              />
              {watchedModalType === 'comment' && (
                <Controller
                  name='modalCommentVariable'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      value={
                        variableOptionsForOnVariableChange.find(v => v.internalId === field.value) || null
                      }
                      onChange={(_, newValue: any) => {
                        field.onChange(newValue?.internalId ?? '')
                      }}
                      options={variableOptionsForOnVariableChange}
                      groupBy={(option: any) => {
                        const scope = option.variableScope
                        if (scope === 'background') return 'Background Variables'
                        if (scope === 'sheetForm') return 'Sheet Form Variables'
                        return option.isGlobal ? 'Global Variables' : 'Local Variables'
                      }}
                      getOptionLabel={(option: any) => option.internalId ?? ''}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label='Variable for comment'
                          helperText='Form variable where the comment will be saved (e.g. comment, driver_comment)'
                        />
                      )}
                    />
                  )}
                />
              )}
              {watchedModalType === 'picker' && (
                <>
                  <Controller
                    name='modalSourceVariable'
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        value={
                          variableOptionsForOnVariableChange.find(v => v.internalId === field.value) || null
                        }
                        onChange={(_, newValue: any) => {
                          field.onChange(newValue?.internalId ?? '')
                        }}
                        options={variableOptionsForOnVariableChange}
                        groupBy={(option: any) => {
                          const scope = option.variableScope
                          if (scope === 'background') return 'Background Variables'
                          if (scope === 'sheetForm') return 'Sheet Form Variables'
                          return option.isGlobal ? 'Global Variables' : 'Local Variables'
                        }}
                        getOptionLabel={(option: any) => option.internalId ?? ''}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label='Source variable (multiple)'
                            helperText='Variable with array of options (e.g. list of statuses, drivers)'
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name='modalDisplayedValue'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Displayed value (field path)'
                        fullWidth
                        placeholder='e.g. label, name, title'
                        helperText='Field path of each item to show in the picker (leave empty to use String(item))'
                      />
                    )}
                  />
                  <Controller
                    name='modalTargetVariable'
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        value={
                          variableOptionsForOnVariableChange.find(v => v.internalId === field.value) || null
                        }
                        onChange={(_, newValue: any) => {
                          field.onChange(newValue?.internalId ?? '')
                        }}
                        options={variableOptionsForOnVariableChange}
                        groupBy={(option: any) => {
                          const scope = option.variableScope
                          if (scope === 'background') return 'Background Variables'
                          if (scope === 'sheetForm') return 'Sheet Form Variables'
                          return option.isGlobal ? 'Global Variables' : 'Local Variables'
                        }}
                        getOptionLabel={(option: any) => option.internalId ?? ''}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label='Target variable'
                            helperText='Variable to store the selected value'
                          />
                        )}
                      />
                    )}
                  />
                </>
              )}
              <Box>
                <Controller
                  name='showModalIfJs'
                  control={control}
                  render={({ field }) => (
                    <>
                      <Button
                        fullWidth
                        variant='outlined'
                        onClick={() => setShowShowModalIfJsDialog(true)}
                        startIcon={<CodeIcon />}
                      >
                        {field.value
                          ? 'Edit Show modal if (JS)'
                          : 'Add Show modal if (JS)'}
                      </Button>
                      {field.value && (
                        <Box mt={1}>
                          <Box
                            p={1}
                            sx={{
                              backgroundColor: '#f5f5f5',
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant='caption'
                              component='pre'
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: 11,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                margin: 0,
                              }}
                            >
                              {field.value.length > 100
                                ? field.value.substring(0, 100) + '...'
                                : field.value}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                />
                <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                  If empty, modal is always shown. If set, modal opens only when the expression returns true.
                </Typography>
              </Box>
            </>
          )}

          {/* Drawer item selector for ON_DRAWER_ITEM_PRESS */}
          {needsDrawerItemSelection && (
            <Controller
              name='drawerItemIndex'
              control={control}
              rules={{ required: 'Drawer item is required' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  disabled={lockElementSelection}
                  value={
                    drawerItems.find((_, i) => i === field.value) != null
                      ? {
                          index: field.value as number,
                          label: drawerItems[field.value as number]?.label ?? '',
                          internalId: drawerItems[field.value as number]?.internalId ?? '',
                        }
                      : null
                  }
                  onChange={(_, newValue: { index: number; label: string; internalId: string } | null) => {
                    field.onChange(newValue?.index ?? null)
                  }}
                  options={drawerItems.map((item, index) => ({
                    index,
                    label: item.label || `Item ${index + 1}`,
                    internalId: item.internalId ?? '',
                  }))}
                  getOptionLabel={option =>
                    `${option.index + 1}. ${option.internalId ? `[${option.internalId}] ` : ''}${option.label}`
                  }
                  isOptionEqualToValue={(a, b) => a.index === b.index}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Drawer Item'
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        (lockElementSelection
                          ? 'Drawer item is fixed for this flow (created from element modal)'
                          : 'Select which drawer item triggers this event')
                      }
                    />
                  )}
                />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='submit' variant='contained'>
            Save
          </Button>
        </DialogActions>
      </form>

      {showShowModalIfJsDialog && (
        <ScriptValueDialog
          isShow
          value={watch('showModalIfJs') || ''}
          title='Show modal if (JS)'
          onClose={() => setShowShowModalIfJsDialog(false)}
          onSave={(code: string) => {
            setValue('showModalIfJs', code)
            setShowShowModalIfJsDialog(false)
          }}
          language='js'
          width={800}
          variables={scriptVariablesForShowModalIf}
          fieldsInfo={scriptFieldsInfoForShowModalIf}
          examples={[
            {
              title: 'Boolean check',
              code: 'return eventData.item?.status === "NEW"',
            },
            {
              title: 'Form data',
              code: 'return eventData.formData?.status === "active"',
            },
            {
              title: 'Always show modal',
              code: 'return true',
            },
          ]}
        />
      )}
    </Dialog>
  )
}

